import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Sparkles, Upload, X, Loader2, Wand2, Video, Image as ImageIcon, RefreshCw,
  Trash2, Download, Copy, Check, AlertCircle, Music, Hash,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { mediaGenAPI } from '../lib/api';

const MODES = [
  { value: 'text-to-video',      label: 'Text to Video',      icon: Wand2,
    description: 'Generate a video from a text prompt only.' },
  { value: 'image-to-video',     label: 'Image to Video',     icon: ImageIcon,
    description: 'Animate a single still image with a text direction.' },
  { value: 'reference-to-video', label: 'Reference to Video', icon: Video,
    description: 'Up to 9 images + 3 videos + 3 audios as multi-reference.' },
];

const DURATIONS = ['auto', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24', '25', '26', '27', '28', '29', '30'];
const RESOLUTIONS = ['480p', '720p'];
const ASPECT_RATIOS = ['auto', '21:9', '16:9', '4:3', '1:1', '3:4', '9:16'];

export default function MediaGenerator() {
  const [mode, setMode] = useState('text-to-video');
  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState('auto');
  const [resolution, setResolution] = useState('720p');
  const [aspectRatio, setAspectRatio] = useState('auto');
  const [generateAudio, setGenerateAudio] = useState(true);
  const [seed, setSeed] = useState('');
  // refs: each entry = { kind, url, filename, size_bytes, ... }
  const [refImages, setRefImages] = useState([]);
  const [refVideos, setRefVideos] = useState([]);
  const [refAudios, setRefAudios] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);

  const imgInputRef = useRef(null);
  const vidInputRef = useRef(null);
  const audInputRef = useRef(null);

  // ===== Load history =====
  const loadJobs = async () => {
    try {
      const res = await mediaGenAPI.list({ limit: 50 });
      setJobs(res.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load history');
    } finally {
      setLoadingJobs(false);
    }
  };

  useEffect(() => { loadJobs(); }, []);

  // Auto-poll any non-terminal jobs every 8s
  useEffect(() => {
    const pending = jobs.filter((j) => j.status === 'queued' || j.status === 'processing');
    if (pending.length === 0) return undefined;
    const t = setInterval(async () => {
      for (const j of pending.slice(0, 5)) {
        try {
          const res = await mediaGenAPI.refresh(j.id);
          setJobs((prev) => prev.map((p) => (p.id === j.id ? res.data : p)));
        } catch (err) {
          // silent
        }
      }
    }, 8000);
    return () => clearInterval(t);
  }, [jobs]);

  // ===== Upload reference =====
  const handleUpload = async (e, kind) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const limits = { image: 9, video: 3, audio: 3 };
    const current = kind === 'image' ? refImages : kind === 'video' ? refVideos : refAudios;
    if (current.length + files.length > limits[kind]) {
      toast.error(`Max ${limits[kind]} ${kind}s per generation`);
      return;
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const res = await mediaGenAPI.uploadReference(f, kind);
        uploaded.push(res.data);
      }
      if (kind === 'image') setRefImages((p) => [...p, ...uploaded]);
      if (kind === 'video') setRefVideos((p) => [...p, ...uploaded]);
      if (kind === 'audio') setRefAudios((p) => [...p, ...uploaded]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeRef = (kind, idx) => {
    if (kind === 'image') setRefImages((p) => p.filter((_, i) => i !== idx));
    if (kind === 'video') setRefVideos((p) => p.filter((_, i) => i !== idx));
    if (kind === 'audio') setRefAudios((p) => p.filter((_, i) => i !== idx));
  };

  // ===== Submit =====
  const handleGenerate = async () => {
    if (!prompt.trim()) { toast.error('Prompt is required'); return; }
    if (mode === 'image-to-video' && refImages.length === 0) {
      toast.error('Image-to-video needs a reference image'); return;
    }
    if (mode === 'reference-to-video' && refImages.length === 0 && refVideos.length === 0 && refAudios.length === 0) {
      toast.error('Reference-to-video needs at least one reference'); return;
    }
    try {
      setSubmitting(true);
      const payload = {
        mode,
        prompt: prompt.trim(),
        duration,
        resolution,
        aspect_ratio: aspectRatio,
        generate_audio: generateAudio,
        seed: seed ? Number(seed) : undefined,
        reference_images: refImages.map((r) => r.url),
        reference_videos: refVideos.map((r) => r.url),
        reference_audios: refAudios.map((r) => r.url),
      };
      const res = await mediaGenAPI.generate(payload);
      toast.success('Generation submitted — videos take ~30s to 3min');
      setJobs((p) => [res.data, ...p]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const showImageRefs = mode === 'image-to-video' || mode === 'reference-to-video';
  const showVideoRefs = mode === 'reference-to-video';
  const showAudioRefs = mode === 'reference-to-video';

  return (
    <div className="min-h-screen bg-zinc-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900 inline-flex items-center gap-2">
              <Sparkles size={22} className="text-violet-600" /> Media Generator
            </h1>
            <p className="text-sm text-zinc-500 mt-0.5">
              Bytedance Seedance 2.5 — text, image, or multi-reference video (up to 30s). Outputs auto-mirror to R2.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={loadJobs} data-testid="mediagen-refresh-list">
            <RefreshCw size={13} className="mr-1.5" /> Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Left: Generation form */}
          <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-xl p-4 space-y-4 h-fit" data-testid="mediagen-form">
            <Tabs value={mode} onValueChange={(v) => { setMode(v); setRefVideos([]); setRefAudios([]); }}>
              <TabsList className="grid grid-cols-3 bg-zinc-100 w-full">
                {MODES.map((m) => (
                  <TabsTrigger key={m.value} value={m.value} data-testid={`mediagen-mode-${m.value}`}>
                    <m.icon size={12} className="mr-1.5" />
                    <span className="hidden sm:inline">{m.label}</span>
                    <span className="sm:hidden">{m.label.split(' ')[0]}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
              {MODES.map((m) => (
                <TabsContent key={m.value} value={m.value} className="text-[11px] text-zinc-500 px-1">
                  {m.description}
                </TabsContent>
              ))}
            </Tabs>

            {/* References */}
            {showImageRefs && (
              <RefSection
                title={mode === 'image-to-video' ? 'Reference Image (1)' : `Reference Images (${refImages.length}/9)`}
                items={refImages}
                onAdd={() => imgInputRef.current?.click()}
                onRemove={(i) => removeRef('image', i)}
                kind="image"
                limit={mode === 'image-to-video' ? 1 : 9}
                disabled={uploading || (mode === 'image-to-video' ? refImages.length >= 1 : refImages.length >= 9)}
              />
            )}
            {showVideoRefs && (
              <RefSection
                title={`Reference Videos (${refVideos.length}/3)`}
                items={refVideos}
                onAdd={() => vidInputRef.current?.click()}
                onRemove={(i) => removeRef('video', i)}
                kind="video"
                limit={3}
                disabled={uploading || refVideos.length >= 3}
              />
            )}
            {showAudioRefs && (
              <RefSection
                title={`Reference Audios (${refAudios.length}/3)`}
                items={refAudios}
                onAdd={() => audInputRef.current?.click()}
                onRemove={(i) => removeRef('audio', i)}
                kind="audio"
                limit={3}
                disabled={uploading || refAudios.length >= 3}
              />
            )}

            <input ref={imgInputRef} type="file" accept="image/*" multiple={mode !== 'image-to-video'} className="hidden" onChange={(e) => handleUpload(e, 'image')} />
            <input ref={vidInputRef} type="file" accept="video/*" multiple className="hidden" onChange={(e) => handleUpload(e, 'video')} />
            <input ref={audInputRef} type="file" accept="audio/*" multiple className="hidden" onChange={(e) => handleUpload(e, 'audio')} />

            {/* Prompt */}
            <div>
              <label className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">Prompt *</label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={mode === 'reference-to-video'
                  ? '@Image1 shows the ring, @Image2 shows the model. Slow dolly-in with warm lighting, the ring rotates...'
                  : 'A cinematic close-up of a marquise diamond ring rotating slowly under warm lighting, shallow depth of field...'}
                className="w-full mt-1 text-sm border border-zinc-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-100 focus:border-violet-300 resize-none placeholder:text-zinc-400"
                rows={4}
                data-testid="mediagen-prompt"
              />
              {mode === 'reference-to-video' && (
                <p className="text-[10px] text-zinc-500 mt-1">
                  💡 Tip: Reference your inputs in the prompt with <code className="bg-zinc-100 px-1 rounded">@Image1</code>, <code className="bg-zinc-100 px-1 rounded">@Image2</code>… <code className="bg-zinc-100 px-1 rounded">@Video1</code>, <code className="bg-zinc-100 px-1 rounded">@Audio1</code> — Seedance composes them together based on how you describe them.
                </p>
              )}
            </div>
            <details className="text-xs text-zinc-600">
              <summary className="cursor-pointer select-none text-zinc-500 hover:text-zinc-700">Advanced options</summary>
              <div className="mt-2 space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <FieldSelect label="Duration" value={duration} onChange={setDuration} options={DURATIONS} testId="mediagen-duration" />
                  <FieldSelect label="Resolution" value={resolution} onChange={setResolution} options={RESOLUTIONS} testId="mediagen-resolution" />
                  <FieldSelect label="Aspect" value={aspectRatio} onChange={setAspectRatio} options={ASPECT_RATIOS} testId="mediagen-ar" />
                </div>
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">Seed (optional)</label>
                  <Input
                    type="number" value={seed}
                    onChange={(e) => setSeed(e.target.value)}
                    placeholder="random"
                    className="text-sm"
                    data-testid="mediagen-seed"
                  />
                </div>
                <label className="flex items-center gap-2 text-[12px] text-zinc-700 cursor-pointer">
                  <input
                    type="checkbox" checked={generateAudio}
                    onChange={(e) => setGenerateAudio(e.target.checked)}
                    data-testid="mediagen-gen-audio"
                  />
                  <span>Generate audio (free, included)</span>
                </label>
              </div>
            </details>

            <Button
              onClick={handleGenerate}
              disabled={submitting || uploading || !prompt.trim()}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="mediagen-submit"
            >
              {submitting
                ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Submitting…</>
                : uploading
                ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Uploading references…</>
                : <><Sparkles size={14} className="mr-1.5" /> Generate</>}
            </Button>
          </div>

          {/* Right: History */}
          <div className="lg:col-span-3 space-y-3" data-testid="mediagen-history">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-zinc-700 uppercase tracking-wider">History</h2>
              {jobs.length > 0 && (
                <span className="text-[11px] text-zinc-500">{jobs.length} generation{jobs.length === 1 ? '' : 's'}</span>
              )}
            </div>
            {loadingJobs ? (
              <div className="bg-white border border-zinc-200 rounded-xl p-12 text-center">
                <Loader2 className="animate-spin mx-auto text-zinc-400" />
              </div>
            ) : jobs.length === 0 ? (
              <div className="bg-white border border-zinc-200 rounded-xl p-12 text-center space-y-2">
                <Sparkles size={20} className="text-zinc-300 mx-auto" />
                <div className="text-sm text-zinc-500">No generations yet — write a prompt and hit Generate.</div>
              </div>
            ) : (
              jobs.map((j) => <JobCard key={j.id} job={j} onDelete={async () => {
                if (!window.confirm('Delete this generation? Output video will be removed.')) return;
                try {
                  await mediaGenAPI.delete(j.id);
                  setJobs((p) => p.filter((x) => x.id !== j.id));
                  toast.success('Deleted');
                } catch (err) {
                  toast.error('Delete failed');
                }
              }} onRefresh={async () => {
                try {
                  const res = await mediaGenAPI.refresh(j.id);
                  setJobs((p) => p.map((x) => (x.id === j.id ? res.data : x)));
                  if (res.data?.status === 'completed') toast.success('Video ready');
                  else if (res.data?.status === 'failed') toast.error(res.data.error || 'Generation failed');
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Refresh failed');
                }
              }} onCancel={async () => {
                if (!window.confirm('Cancel this generation? You will not be charged for the cancelled job (best effort).')) return;
                try {
                  const res = await mediaGenAPI.cancel(j.id);
                  setJobs((p) => p.map((x) => (x.id === j.id ? res.data : x)));
                  toast.success('Job cancelled');
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Cancel failed');
                }
              }} />)
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm border border-zinc-200 rounded-md px-2 py-1.5 bg-white text-zinc-900"
        data-testid={testId}
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function RefSection({ title, items, onAdd, onRemove, kind, disabled }) {
  const Icon = kind === 'image' ? ImageIcon : kind === 'video' ? Video : Music;
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">{title}</label>
        <Button
          variant="outline" size="sm"
          onClick={onAdd}
          disabled={disabled}
          className="h-7 text-[11px]"
          data-testid={`mediagen-add-${kind}`}
        >
          <Upload size={11} className="mr-1" /> Add
        </Button>
      </div>
      {items.length > 0 ? (
        <div className="grid grid-cols-3 gap-2">
          {items.map((it, i) => (
            <div key={i} className="relative group bg-zinc-100 border border-zinc-200 rounded-md aspect-square overflow-hidden">
              {kind === 'image' ? (
                <img src={it.url} alt={it.filename} className="w-full h-full object-cover" />
              ) : kind === 'video' ? (
                <video src={it.url} className="w-full h-full object-cover" muted />
              ) : (
                <div className="flex items-center justify-center h-full text-[10px] text-zinc-500 px-1 text-center">
                  <Icon size={20} className="opacity-60" />
                </div>
              )}
              <button
                onClick={() => onRemove(i)}
                className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"
                data-testid={`mediagen-remove-${kind}-${i}`}
              >
                <X size={10} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-[10px] text-zinc-400 px-1">No {kind}s attached.</div>
      )}
    </div>
  );
}

function JobCard({ job, onDelete, onRefresh, onCancel }) {
  const [copied, setCopied] = useState(false);
  const videoUrl = job.output_video_r2_url || job.output_video_url;
  const statusColor = {
    queued:     'bg-zinc-100 text-zinc-700',
    processing: 'bg-blue-100 text-blue-800 animate-pulse',
    completed:  'bg-emerald-100 text-emerald-800',
    failed:     'bg-red-100 text-red-800',
  }[job.status] || 'bg-zinc-100 text-zinc-700';
  const created = job.created_at ? new Date(job.created_at).toLocaleString() : '—';
  const cost = job.actual_cost_usd ?? job.estimated_cost_usd;

  // Has the job been queued/processing for an unusually long time?
  const stuckMinutes = (() => {
    if (job.status !== 'queued' && job.status !== 'processing') return 0;
    if (!job.created_at) return 0;
    return Math.floor((Date.now() - new Date(job.created_at).getTime()) / 60000);
  })();
  const isStuck = stuckMinutes >= 15;

  const copyPrompt = () => {
    navigator.clipboard.writeText(job.prompt);
    setCopied(true);
    toast.success('Prompt copied');
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-3 space-y-2" data-testid={`mediagen-job-${job.id}`}>
      <div className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-wider">
        <span className={`px-2 py-0.5 rounded-full font-semibold ${statusColor}`}>{job.status}</span>
        <span className="text-zinc-500">{job.mode}</span>
        <span className="text-zinc-400">·</span>
        <span className="text-zinc-500">{[job.tier && job.tier !== 'standard' ? job.tier : null, job.params?.resolution, job.params?.aspect_ratio, `${job.params?.duration}s`].filter(Boolean).join(' · ')}</span>
        {job.owner_email && (
          <span
            className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-100 normal-case tracking-normal font-medium"
            title={job.owner_email}
            data-testid={`mediagen-owner-${job.id}`}
          >
            by {job.owner_name || job.owner_email.split('@')[0]}
          </span>
        )}
        <span className="ml-auto text-zinc-400 normal-case tracking-normal text-[11px]">{created}</span>
      </div>

      {/* Output */}
      {job.status === 'completed' && videoUrl ? (
        <video src={videoUrl} controls className="w-full rounded-md bg-black max-h-80" data-testid={`mediagen-output-${job.id}`} />
      ) : job.status === 'failed' ? (
        <div className="bg-red-50 border border-red-100 rounded-md p-2 text-[11px] text-red-900 inline-flex items-start gap-1.5">
          <AlertCircle size={12} className="text-red-600 mt-0.5 flex-shrink-0" />
          {job.error || 'Generation failed'}
        </div>
      ) : (
        <div className="bg-zinc-50 border border-dashed border-zinc-200 rounded-md py-8 px-4 text-center text-[12px] text-zinc-500 space-y-2">
          <div>
            <Loader2 size={14} className="animate-spin inline mr-1.5" />
            {job.status === 'queued' ? 'Queued on Runway…' : 'Generating video — usually 30s to 3 minutes…'}
          </div>
          {stuckMinutes > 0 && (
            <div className={`text-[11px] ${isStuck ? 'text-amber-700' : 'text-zinc-400'}`}>
              {isStuck ? '⚠ ' : ''}Stuck for {stuckMinutes} min{job.fal_status ? ` · Runway status: ${job.fal_status}` : ''}
            </div>
          )}
          {job.last_refresh_error && (
            <div className="text-[11px] text-red-600">Last error: {job.last_refresh_error}</div>
          )}
        </div>
      )}

      {/* Prompt */}
      <div className="space-y-1">
        <div className="flex items-center gap-1.5">
          <Hash size={10} className="text-zinc-400" />
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">Prompt</span>
          <button onClick={copyPrompt} className="text-zinc-400 hover:text-zinc-700 ml-1">
            {copied ? <Check size={10} className="text-emerald-600" /> : <Copy size={10} />}
          </button>
        </div>
        <div className="text-[12px] text-zinc-700 leading-snug bg-zinc-50 rounded-md p-2 whitespace-pre-wrap">
          {job.prompt}
        </div>
        {job.negative_prompt && (
          <div className="text-[11px] text-red-900/70 bg-red-50/30 rounded-md p-1.5">
            <span className="font-semibold">Negative:</span> {job.negative_prompt}
          </div>
        )}
      </div>

      {/* References */}
      {(job.reference_images?.length > 0 || job.reference_videos?.length > 0 || job.reference_audios?.length > 0) && (
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">References</div>
          <div className="flex flex-wrap gap-1.5">
            {(job.reference_images || []).map((u, i) => (
              <img key={`i${i}`} src={u} alt="" className="w-10 h-10 rounded object-cover bg-zinc-100" />
            ))}
            {(job.reference_videos || []).map((u, i) => (
              <video key={`v${i}`} src={u} muted className="w-10 h-10 rounded object-cover bg-zinc-100" />
            ))}
            {(job.reference_audios || []).map((u, i) => (
              <div key={`a${i}`} className="w-10 h-10 rounded bg-zinc-100 flex items-center justify-center">
                <Music size={12} className="text-zinc-400" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metadata row */}
      <div className="flex items-center justify-between flex-wrap gap-2 text-[11px] text-zinc-500 pt-1 border-t border-zinc-100">
        <div className="flex items-center gap-3 flex-wrap">
          <span><span className="text-zinc-400">Cost:</span> <span className="font-mono text-zinc-700">${(cost || 0).toFixed(4)}</span></span>
          {job.output_seed != null && (
            <span><span className="text-zinc-400">Seed:</span> <span className="font-mono text-zinc-700">{job.output_seed}</span></span>
          )}
          {job.output_size_bytes && (
            <span><span className="text-zinc-400">Size:</span> <span className="font-mono text-zinc-700">{(job.output_size_bytes / 1024 / 1024).toFixed(1)} MB</span></span>
          )}
          {job.output_video_r2_key && (
            <span className="text-emerald-700">✓ R2-mirrored</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {(job.status === 'queued' || job.status === 'processing') && (
            <>
              <Button variant="ghost" size="sm" onClick={onRefresh} className="h-7 text-[11px] text-zinc-500" data-testid={`mediagen-refresh-${job.id}`}>
                <RefreshCw size={11} className="mr-1" /> Refresh
              </Button>
              {isStuck && onCancel && (
                <Button variant="ghost" size="sm" onClick={onCancel} className="h-7 text-[11px] text-amber-700 hover:text-amber-900 hover:bg-amber-50" data-testid={`mediagen-cancel-${job.id}`}>
                  Cancel
                </Button>
              )}
            </>
          )}
          {videoUrl && (
            <a
              href={videoUrl} download target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50"
              data-testid={`mediagen-download-${job.id}`}
            >
              <Download size={11} /> Download
            </a>
          )}
          <Button variant="ghost" size="sm" onClick={onDelete} className="h-7 text-zinc-400 hover:text-red-600" data-testid={`mediagen-delete-${job.id}`}>
            <Trash2 size={11} />
          </Button>
        </div>
      </div>
    </div>
  );
}
